<!-- warehouse_scan/public/js/components/ScanInterface.vue -->
<template>
  <div class="scan-interface">
    <!-- 头部 -->
    <div class="header">
      <div class="header-title">
        <h3>📱 扫码进出库</h3>
        <a href="/app" class="back-btn">返回</a>
      </div>
    </div>

    <!-- 类型选择组件 -->
    <TypeSelector 
      v-model:type="scanType" 
      v-model:subtype="subType"
      @change="handleTypeChange"
    />

    <!-- 扫描区域组件 -->
    <ScanArea 
      ref="scanAreaRef"
      :mode="scanMode"
      :disabled="!canScan"
      @scan="handleScan"
      @mode-change="handleModeChange"
    />

    <!-- 库位锁定指示器 -->
    <div v-if="currentWarehouse" class="warehouse-lock">
      <div class="warehouse-info">
        <span class="warehouse-icon">📍</span>
        <span class="warehouse-code">{{ currentWarehouse }}</span>
      </div>
      <button class="unlock-btn" @click="unlockWarehouse">解锁</button>
    </div>

    <!-- 草稿单据指示器 -->
    <div v-if="draftDocument" class="draft-banner">
      <div class="draft-info">
        <span class="draft-icon">📋</span>
        <div class="draft-detail">
          <span class="draft-doctype">{{ draftDocument.doctype }}</span>
          <span class="draft-docname">{{ draftDocument.docname }}</span>
          <span class="draft-stats">{{ draftDocument.item_count }} 行 / {{ draftDocument.total_qty }} 件</span>
        </div>
      </div>
      <div class="draft-actions">
        <button class="draft-btn submit-btn" @click="submitDraft" :disabled="isSubmitting">确认提交</button>
        <button class="draft-btn cancel-btn" @click="cancelDraft" :disabled="isSubmitting">取消</button>
      </div>
    </div>

    <!-- 盘点模式工具栏 -->
    <div v-if="isInventoryMode" class="inventory-toolbar">
      <button class="inventory-btn" @click="initFullCount" :disabled="!currentWarehouse || isLoading">
        全仓盘点
      </button>
      <button class="inventory-btn" @click="showStockBalance" :disabled="!currentWarehouse">
        查看库存
      </button>
    </div>

    <!-- 扫描结果组件 -->
    <ScanResult 
      v-if="scanResult"
      :result="scanResult"
      @confirm="handleResultConfirm"
    />

    <!-- 历史记录组件 -->
    <HistoryList 
      :history="scanHistory"
      @clear="clearHistory"
      @item-click="viewHistoryDetail"
    />

    <!-- 数量输入弹窗 -->
    <QtyDialog 
      v-if="showQtyDialog"
      :item="pendingItem"
      :max-qty="maxQty"
      :allow-zero="isInventoryMode"
      @confirm="handleQtyConfirm"
      @cancel="handleQtyCancel"
    />

    <!-- 详情弹窗 -->
    <Dialog 
      v-if="showDetailDialog"
      title="扫描详情"
      @close="showDetailDialog = false"
    >
      <div class="detail-content">
        <div v-for="(value, key) in detailFields" :key="key" class="detail-row">
          <span class="detail-label">{{ key }}:</span>
          <span class="detail-value">{{ value }}</span>
        </div>
      </div>
    </Dialog>

    <!-- 加载指示器 -->
    <LoadingOverlay v-if="isLoading" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import TypeSelector from './TypeSelector.vue';
import ScanArea from './ScanArea.vue';
import ScanResult from './ScanResult.vue';
import HistoryList from './HistoryList.vue';
import QtyDialog from './QtyDialog.vue';
import Dialog from './Dialog.vue';
import LoadingOverlay from './LoadingOverlay.vue';

// Props
const props = defineProps({
  scanType: {
    type: String,
    default: null
  },
  subType: {
    type: String,
    default: null
  },
  warehouse: {
    type: String,
    default: null
  },
  onScan: {
    type: Function,
    default: null
  },
  onComplete: {
    type: Function,
    default: null
  }
});

// Emits
const emit = defineEmits(['update:scanType', 'update:subType', 'scan', 'complete']);

// ============ 状态 ============
const scanType = ref(props.scanType);
const subType = ref(props.subType);
const scanMode = ref('scanner');
const currentWarehouse = ref(props.warehouse);
const scanHistory = ref([]);
const scanResult = ref(null);
const isLoading = ref(false);
const showQtyDialog = ref(false);
const pendingItem = ref(null);
const maxQty = ref(0);
const showDetailDialog = ref(false);
const selectedHistory = ref(null);
const draftDocument = ref(null);
const isSubmitting = ref(false);

// 盘点模式判断
const isInventoryMode = computed(() => subType.value === 'count' || subType.value === 'adjust');

// 引用
const scanAreaRef = ref(null);

// ============ 计算属性 ============
const canScan = computed(() => {
  return scanType.value && subType.value;
});

const detailFields = computed(() => {
  if (!selectedHistory.value) return {};
  
  const item = selectedHistory.value;
  return {
    '时间': item.time?.toLocaleString() || '-',
    '条码': item.value || '-',
    '类型': item.type || '-',
    '消息': item.message || '-',
    '创建单据': item.createdDocument || '-'
  };
});

// ============ 方法 ============

// 初始化
const init = () => {
  loadHistory();
  initFromUrl();
  
  // 如果有初始类型，自动聚焦
  if (scanType.value && subType.value) {
    setTimeout(() => {
      if (scanAreaRef.value) {
        scanAreaRef.value.focus();
      }
    }, 500);
  }
};

// 从URL参数初始化
const initFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const type = params.get('type');
  const subtype = params.get('subtype');
  
  if (type) scanType.value = type;
  if (subtype) subType.value = subtype;
};

// 处理类型变化
const handleTypeChange = (type, subtype) => {
  scanType.value = type;
  subType.value = subtype;
  draftDocument.value = null;
  loadDraft();
  
  if (props.onScan) {
    props.onScan({ type, subtype });
  }
};

// 加载草稿单据
const loadDraft = async () => {
  if (!subType.value) return;
  try {
    const response = await frappe.call({
      method: 'warehouse_scan.api.get_current_draft',
      args: { sub_type: subType.value }
    });
    draftDocument.value = response.message?.data || null;
  } catch (e) {
    draftDocument.value = null;
  }
};

// 提交草稿单据
const submitDraft = async () => {
  if (!subType.value || !draftDocument.value) return;
  isSubmitting.value = true;
  try {
    const response = await frappe.call({
      method: 'warehouse_scan.api.submit_current_document',
      args: { sub_type: subType.value }
    });
    if (response.message?.success) {
      frappe.show_alert({
        message: response.message.message,
        indicator: 'green'
      });
      addToHistory({
        value: draftDocument.value.docname,
        type: 'SUBMIT',
        message: response.message.message,
        createdDocument: draftDocument.value.docname,
        status: 'success'
      });
      draftDocument.value = null;
      playBeep('success');
    }
  } catch (error) {
    frappe.show_alert({
      message: error._error_message || '提交失败',
      indicator: 'red'
    });
    playBeep('error');
  } finally {
    isSubmitting.value = false;
  }
};

// 取消草稿单据
const cancelDraft = async () => {
  if (!subType.value || !draftDocument.value) return;
  isSubmitting.value = true;
  try {
    const response = await frappe.call({
      method: 'warehouse_scan.api.cancel_current_document',
      args: { sub_type: subType.value }
    });
    if (response.message?.success) {
      frappe.show_alert({
        message: response.message.message,
        indicator: 'blue'
      });
      draftDocument.value = null;
    }
  } catch (error) {
    frappe.show_alert({
      message: error._error_message || '取消失败',
      indicator: 'red'
    });
  } finally {
    isSubmitting.value = false;
  }
};

// 全仓盘点初始化
const initFullCount = async () => {
  if (!currentWarehouse.value) return;
  isLoading.value = true;
  try {
    const response = await frappe.call({
      method: 'warehouse_scan.api.init_warehouse_count',
      args: { warehouse: currentWarehouse.value, sub_type: subType.value }
    });
    if (response.message?.success) {
      const data = response.message.data;
      // 显示库存清单，提示用户逐项扫码录入
      let content = `<div style="max-height:400px;overflow-y:auto">`;
      content += `<p style="margin-bottom:10px;color:#666">请逐项扫描物料码，输入实际盘点数量</p>`;
      content += `<table style="width:100%;font-size:12px;border-collapse:collapse">`;
      content += `<tr style="background:#f0f0f0"><th style="padding:6px;text-align:left">物料</th><th style="padding:6px">批次</th><th style="padding:6px">账面数量</th></tr>`;
      for (const item of (data.items || [])) {
        content += `<tr style="border-bottom:1px solid #eee">`;
        content += `<td style="padding:6px">${item.item_code}<br><small style="color:#888">${item.item_name || ''}</small></td>`;
        content += `<td style="padding:6px;text-align:center">${item.batch_no || '-'}</td>`;
        content += `<td style="padding:6px;text-align:center">${item.current_qty}</td>`;
        content += `</tr>`;
      }
      content += `</table></div>`;
      
      frappe.msgprint({
        title: `${currentWarehouse.value} 库存清单（共 ${data.total_items} 项）`,
        message: content,
        indicator: 'blue'
      });
      playBeep('success');
    }
  } catch (error) {
    frappe.show_alert({
      message: error._error_message || '初始化盘点失败',
      indicator: 'red'
    });
    playBeep('error');
  } finally {
    isLoading.value = false;
  }
};

// 查看库存余额
const showStockBalance = async () => {
  if (!currentWarehouse.value) return;
  isLoading.value = true;
  try {
    const response = await frappe.call({
      method: 'warehouse_scan.api.get_warehouse_stock_balance',
      args: { warehouse: currentWarehouse.value }
    });
    if (response.message) {
      const balances = response.message;
      let content = `<div style="max-height:400px;overflow-y:auto">`;
      content += `<table style="width:100%;font-size:12px;border-collapse:collapse">`;
      content += `<tr style="background:#f0f0f0"><th style="padding:6px;text-align:left">物料</th><th style="padding:6px">库存数量</th></tr>`;
      for (const item of balances) {
        content += `<tr style="border-bottom:1px solid #eee">`;
        content += `<td style="padding:6px">${item.item_code}<br><small style="color:#888">${item.item_name || ''}</small></td>`;
        content += `<td style="padding:6px;text-align:center">${item.current_qty}</td>`;
        content += `</tr>`;
      }
      content += `</table></div>`;
      
      // 使用 frappe 的消息对话框
      frappe.msgprint({
        title: `${currentWarehouse.value} 库存余额`,
        message: content,
        indicator: 'blue'
      });
    }
  } catch (error) {
    frappe.show_alert({
      message: error._error_message || '获取库存失败',
      indicator: 'red'
    });
  } finally {
    isLoading.value = false;
  }
};

// 处理扫描
const handleScan = async (value) => {
  if (!canScan.value) {
    frappe.show_alert({
      message: '请先选择出入库类型',
      indicator: 'orange'
    });
    return;
  }

  // 入库和出库都需要先扫库位码
  if (!currentWarehouse.value && !value.startsWith('LOC:')) {
    frappe.show_alert({
      message: '请先扫描库位码',
      indicator: 'orange'
    });
    return;
  }

  isLoading.value = true;

  try {
    const response = await frappe.call({
      method: 'warehouse_scan.api.scan_handler',
      args: {
        scan_value: value,
        scan_type: scanType.value,
        sub_type: subType.value,
        warehouse: currentWarehouse.value
      }
    });

    if (response.message?.success) {
      handleScanSuccess(response.message, value);
      
      // 触发回调
      if (props.onScan) {
        props.onScan(response.message);
      }
    } else {
      handleScanError(response.message?.message || '扫描处理失败', value);
    }
  } catch (error) {
    console.error('扫描失败:', error);
    handleScanError(error._error_message || '网络错误', value);
  } finally {
    isLoading.value = false;
  }
};

// 处理扫描成功
const handleScanSuccess = (result, rawValue) => {
  // 添加到历史
  addToHistory({
    value: rawValue,
    type: result.data?.level || 'UNKNOWN',
    message: result.message,
    data: result.data,
    createdDocument: result.created_document,
    status: 'success'
  });

  // 处理库位
  if (result.data?.level === 'LOCATION') {
    currentWarehouse.value = result.data.warehouse;
    frappe.show_alert({
      message: `库位 ${result.data.warehouse} 已锁定`,
      indicator: 'green'
    });
  }

  // 显示结果
  scanResult.value = result;

  // 刷新草稿单据信息
  if (result.draft_document) {
    loadDraft();
  }

  // 需要输入数量
  if (result.need_input) {
    pendingItem.value = result.data;
    maxQty.value = result.data.max_qty;
    // 盘点模式：显示当前库存数量
    if (result.data.current_qty !== undefined) {
      pendingItem.value.current_qty = result.data.current_qty;
    }
    showQtyDialog.value = true;
  }

  // 播放成功提示音
  playBeep('success');
};

// 处理扫描错误
const handleScanError = (message, value) => {
  frappe.show_alert({
    message: message,
    indicator: 'red'
  });

  addToHistory({
    value,
    type: 'ERROR',
    message,
    status: 'failed'
  });

  playBeep('error');
};

// 处理模式切换
const handleModeChange = (mode) => {
  scanMode.value = mode;
};

// 处理数量确认
const handleQtyConfirm = async (qty, batchNo) => {
  showQtyDialog.value = false;
  isLoading.value = true;

  try {
    const response = await frappe.call({
      method: 'warehouse_scan.api.complete_item_scan',
      args: {
        po_item: pendingItem.value?.po_item,
        item_code: pendingItem.value?.item_code,
        qty: qty,
        warehouse: pendingItem.value?.warehouse,
        scan_type: scanType.value,
        sub_type: subType.value,
        batch_no: batchNo || pendingItem.value?.batch_no || null
      }
    });

    if (response.message?.success) {
      frappe.show_alert({
        message: `已添加 ${qty} 个物料`,
        indicator: 'green'
      });
      
      addToHistory({
        value: `${pendingItem.value?.item_code} x ${qty}`,
        type: 'ITEM',
        message: '数量确认',
        createdDocument: response.message.created_document
      });

      if (response.message.created_document) {
        scanResult.value.created_document = response.message.created_document;
      }

      // 触发完成回调
      if (props.onComplete) {
        props.onComplete(response.message);
      }
    }
  } catch (error) {
    frappe.show_alert({
      message: error._error_message || '操作失败',
      indicator: 'red'
    });
  } finally {
    isLoading.value = false;
    pendingItem.value = null;
  }
};

// 处理数量取消
const handleQtyCancel = () => {
  showQtyDialog.value = false;
  pendingItem.value = null;
};

// 处理结果确认
const handleResultConfirm = () => {
  scanResult.value = null;
};

// 解锁库位
const unlockWarehouse = () => {
  currentWarehouse.value = null;
  frappe.show_alert({
    message: '库位已解锁',
    indicator: 'blue'
  });
};

// 添加到历史
const addToHistory = (item) => {
  scanHistory.value.unshift({
    ...item,
    id: Date.now() + Math.random(),
    time: new Date()
  });

  if (scanHistory.value.length > 100) {
    scanHistory.value.pop();
  }

  saveHistory();
};

// 清空历史
const clearHistory = () => {
  scanHistory.value = [];
  saveHistory();
  frappe.show_alert({
    message: '历史记录已清空',
    indicator: 'blue'
  });
};

// 查看历史详情
const viewHistoryDetail = (item) => {
  selectedHistory.value = item;
  showDetailDialog.value = true;
};

// 保存历史到localStorage
const saveHistory = () => {
  try {
    const toSave = scanHistory.value.map(i => ({
      ...i,
      time: i.time.toISOString()
    }));
    localStorage.setItem('warehouse_scan_history', JSON.stringify(toSave));
  } catch (e) {
    console.error('保存历史失败:', e);
  }
};

// 加载历史
const loadHistory = () => {
  try {
    const saved = localStorage.getItem('warehouse_scan_history');
    if (saved) {
      const parsed = JSON.parse(saved);
      scanHistory.value = parsed.map(i => ({
        ...i,
        time: new Date(i.time)
      }));
    }
  } catch (e) {
    console.error('加载历史失败:', e);
  }
};

// 播放提示音
const playBeep = (type) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    if (type === 'success') {
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.1);
    } else {
      oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.2);
    }
  } catch (e) {
    // 忽略
  }
};

// 生命周期
onMounted(() => {
  init();
});

onUnmounted(() => {
  // 清理
});
</script>

<style scoped>
.scan-interface {
  max-width: 600px;
  margin: 0 auto;
  padding: 15px 15px 250px 15px;
  background: #f5f7fa;
}

.header {
  background: white;
  padding: 15px;
  border-radius: 12px;
  margin-bottom: 15px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.header-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-title h3 {
  margin: 0;
  color: #2c3e50;
  font-weight: 600;
}

.back-btn {
  padding: 8px 15px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 20px;
  color: #495057;
  text-decoration: none;
  font-size: 14px;
}

.back-btn:hover {
  background: #e9ecef;
}

.warehouse-lock {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 12px 15px;
  border-radius: 10px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.warehouse-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.warehouse-icon {
  font-size: 20px;
}

.warehouse-code {
  font-weight: 600;
  font-size: 16px;
}

.unlock-btn {
  background: rgba(255,255,255,0.2);
  border: none;
  color: white;
  padding: 5px 12px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 12px;
  backdrop-filter: blur(5px);
}

.draft-banner {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: white;
  padding: 12px 15px;
  border-radius: 10px;
  margin-bottom: 15px;
  box-shadow: 0 4px 15px rgba(17, 153, 142, 0.4);
}

.draft-info {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.draft-icon {
  font-size: 22px;
}

.draft-detail {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.draft-doctype {
  font-size: 11px;
  opacity: 0.85;
}

.draft-docname {
  font-weight: 700;
  font-size: 15px;
}

.draft-stats {
  font-size: 12px;
  opacity: 0.9;
}

.draft-actions {
  display: flex;
  gap: 8px;
}

.draft-btn {
  flex: 1;
  border: none;
  padding: 8px 0;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: opacity 0.2s;
}

.draft-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.submit-btn {
  background: white;
  color: #11998e;
}

.submit-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.cancel-btn {
  background: rgba(255,255,255,0.2);
  color: white;
  backdrop-filter: blur(5px);
}

.cancel-btn:hover:not(:disabled) {
  background: rgba(255,255,255,0.3);
}

.inventory-toolbar {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}

.inventory-btn {
  flex: 1;
  padding: 10px;
  border: 2px solid #17a2b8;
  background: white;
  color: #17a2b8;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.inventory-btn:hover:not(:disabled) {
  background: #17a2b8;
  color: white;
}

.inventory-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.detail-content {
  padding: 10px 0;
}

.detail-row {
  display: flex;
  padding: 8px 0;
  border-bottom: 1px solid #f1f3f5;
}

.detail-label {
  width: 80px;
  color: #6c757d;
  font-size: 13px;
}

.detail-value {
  flex: 1;
  font-weight: 500;
  color: #2c3e50;
}
</style>